import React, { useEffect, useState } from 'react';

import { Button, Checkbox, Confirm, Modal, SpinnerOverlay } from '@components/common';

import { IAttachPortalFileDTO, IGroupOperationAttachPortalDTO } from '@interfaces/portal/dto/foldersManagementDTO';
import { IPortalMilestonesForAttachOption } from '@interfaces/portal/folders';

import { TMenuActionsTypes } from '@enums/common';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { overwriteConfirmText, unassignConfirmText } from './constants';

import styles from './AttachFilesToMilestonesForm.module.scss';

interface IAttachFilesToMilestonesProps {
	milestoneOptions: IPortalMilestonesForAttachOption[];
	closeAttachFilesForm: () => void;
	selectedFilesIds: string[];
	singleFileIdForAttach: string | null;
	attachFilesActionId?: TMenuActionsTypes;
	handleAttachFilesToMilestones: (data: IGroupOperationAttachPortalDTO) => void;
	handleAttachFileToMilestones: (data: IAttachPortalFileDTO) => void;
	isLoading: boolean;
}

const AttachFilesToMilestonesForm = ({
	milestoneOptions,
	closeAttachFilesForm,
	selectedFilesIds,
	attachFilesActionId,
	handleAttachFilesToMilestones,
	singleFileIdForAttach,
	handleAttachFileToMilestones,
	isLoading,
}: IAttachFilesToMilestonesProps) => {
	const [milestonesList, setMilestonesList] = useState(milestoneOptions);
	const [isCheckedAll, setIsCheckedAll] = useState(false);
	const [isIndefinite, setIsIndefinite] = useState(false);
	const [isShowUnassignConfirm, setIsShowUnassignConfirm] = useState(false);
	const [isShowOverwriteConfirm, setIsShowOverwriteConfirm] = useState(false);

	const openUnassignConfirm = () => setIsShowUnassignConfirm(true);
	const closeUnassignConfirm = () => setIsShowUnassignConfirm(false);

	const openOverwriteConfirm = () => setIsShowOverwriteConfirm(true);
	const closeOverwriteConfirm = () => setIsShowOverwriteConfirm(false);

	useEffect(() => {
		const checkedCount = milestonesList.filter(milestone => milestone.checked).length;
		const allChecked = checkedCount === milestonesList.length;
		const someChecked = checkedCount > 0 && checkedCount < milestonesList.length;

		setIsCheckedAll(allChecked);
		setIsIndefinite(someChecked);
	}, [milestonesList]);

	useEffect(() => {
		setMilestonesList(milestoneOptions);
	}, [milestoneOptions]);

	const handleCheckAll = (checked: boolean) => {
		setMilestonesList(prev => prev.map(milestone => ({ ...milestone, checked })));
	};

	const handleChange = (checked: boolean, name: string) => {
		setMilestonesList(prev => {
			return prev.map(milestone =>
				milestone.value === name ? { ...milestone, checked } : milestone,
			);
		});
	};

	const onSubmit = () => {
		const milestoneIds = milestonesList
			.filter(milestone => milestone.checked)
			.map(milestone => milestone.value);

		if (singleFileIdForAttach) {
			handleAttachFileToMilestones({
				id: singleFileIdForAttach,
				milestoneIds,
			});
		} else {
			handleAttachFilesToMilestones({
				ids: selectedFilesIds,
				milestoneIds,
				actionType: attachFilesActionId,
			});
		}

		closeAttachFilesForm();
	};

	const onSave = () => {
		const isAllMilestonesUnassigned = (
			milestoneOptions.some(milestone => milestone.checked)
			&& !isCheckedAll
			&& !isIndefinite
		);

		if (singleFileIdForAttach && isAllMilestonesUnassigned) {
			openUnassignConfirm();

			return;
		}

		if (!singleFileIdForAttach) {
			openOverwriteConfirm();

			return;
		}

		onSubmit();
	};

	return (
		<>
			{isLoading && <SpinnerOverlay />}
			<div className={styles.formContainer}>
				{!singleFileIdForAttach
					&& <div className={styles.helperText}>
						This action will overwrite the current value for each file.
					</div>
				}
				<div className={styles.checkboxContainer}>
					<Checkbox
						checked={isCheckedAll}
						isIndefinite={isIndefinite}
						label='Select all'
						name='selectAll'
						onChange={handleCheckAll}
					/>
					{milestonesList.map(({ value, label, checked }) => {
						return (
							<Checkbox
								key={value}
								checked={checked}
								label={label}
								name={value}
								onChange={handleChange}
							/>
						);
					})}
				</div>
			</div>
			<div className={styles.buttons}>
				<Button
					onClick={closeAttachFilesForm}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={onSave}
					text='Save'
				/>
			</div>
			<Modal
				isOpen={isShowUnassignConfirm}
				onClose={closeUnassignConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={onSubmit}
					question={unassignConfirmText}
				/>
			</Modal>
			<Modal
				isOpen={isShowOverwriteConfirm}
				onClose={closeOverwriteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={onSubmit}
					question={overwriteConfirmText}
				/>
			</Modal>
		</>
	);
};

export default AttachFilesToMilestonesForm;
